import type { ReactNode } from "react";
import { Link } from "wouter";
import type { FcIcon } from "@/components/ds/icons";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";

export interface PageHeaderCta {
  label: string;
  icon?: FcIcon;
  onClick?: () => void;
  href?: string;
}

export interface PageHeaderProps {
  title: string;
  subtitle?: string;
  cta?: PageHeaderCta;
  /** Escape hatch — wins over `cta` when both are supplied. */
  rightContent?: ReactNode;
  /** Optional override on the outer container. */
  className?: string;
}

/**
 * PageHeader is intended ONLY as the page-level header.
 * Use h2/h3 for sub-section headings inside a page.
 */
export function PageHeader({
  title,
  subtitle,
  cta,
  rightContent,
  className,
}: PageHeaderProps) {
  if (import.meta.env.DEV && cta && rightContent) {
    console.warn(
      "[PageHeader] both `cta` and `rightContent` supplied; `rightContent` wins.",
    );
  }

  const right = rightContent ?? (cta ? renderCta(cta) : null);

  return (
    <div
      className={cn(
        "flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between",
        className,
      )}
    >
      <div className="min-w-0">
        <h1 className="fc-page-title">{title}</h1>
        {subtitle && <p className="fc-page-subtitle mt-1">{subtitle}</p>}
      </div>
      {right}
    </div>
  );
}

function renderCta({ label, icon: Icon, onClick, href }: PageHeaderCta) {
  // Ésta es LA acción primaria de la pantalla: el único botón teal permitido.
  const inner = (
    <Button onClick={href ? undefined : onClick} className="shrink-0">
      {Icon && <Icon className="size-4" />}
      {label}
    </Button>
  );
  return href ? <Link href={href}>{inner}</Link> : inner;
}
